import type { DataProvider } from "ra-core";
import { defaultPlanLimits } from "../root/defaultConfiguration";
import type { PlanLimits } from "@slate/shared";

/**
 * Plan-limit enforcement (decision #17). In production this lives below the
 * client (RLS / policies seeded from the control plane); the demo enforces
 * the same rule in the data provider so the behavior is real: past the cap,
 * creates fail with a friendly pointer to Grey Matter.
 */

export const PLAN_LIMITS_MIRROR_KEY = "slate.planLimits";

export function readPlanLimits(): PlanLimits {
  try {
    const raw = localStorage.getItem(PLAN_LIMITS_MIRROR_KEY);
    return raw ? (JSON.parse(raw) as PlanLimits) : defaultPlanLimits;
  } catch {
    return defaultPlanLimits;
  }
}

/** Keep a localStorage mirror so non-React code can read the current limits. */
export function mirrorPlanLimits(limits: PlanLimits) {
  try {
    localStorage.setItem(PLAN_LIMITS_MIRROR_KEY, JSON.stringify(limits));
  } catch {
    // best effort
  }
}

const LIMITED: Record<string, { key: keyof PlanLimits; noun: string }> = {
  contacts: { key: "maxContacts", noun: "contacts" },
  sales: { key: "maxUsers", noun: "user seats" },
};

export function guardDataProviderForPlanLimits<T extends DataProvider>(dp: T): T {
  return {
    ...dp,
    create: async (resource: string, params: unknown) => {
      const rule = LIMITED[resource];
      if (rule) {
        const limits = readPlanLimits();
        const { total } = await dp.getList(resource, {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        });
        if ((total ?? 0) >= limits[rule.key]) {
          throw new Error(
            `You're at your plan's limit of ${limits[rule.key].toLocaleString(
              "en-US",
            )} ${rule.noun}. Reach out to Grey Matter to raise it.`,
          );
        }
      }
      return (dp.create as (r: string, p: unknown) => Promise<never>)(
        resource,
        params,
      );
    },
  } as T;
}
